import { UI_LIBRARY_Z_INDEX, RatingStar } from "@dmm-com/rocket-web-ui-library";
import type { ReactNode, MouseEvent } from "react";
import { useCallback, useState, useRef } from "react";
import * as Styled from "./style";
import type { ItemPackageProps } from "~/components/blocks/common/item-package/type";
import { NextLink } from "~/components/blocks/common/next-link";
import { MINI_DETAIL_STAND_TIME } from "~/const/mini-detail";
import { useMatchMedia } from "~/hooks/use-match-media";
import { useOutsidePointerdown } from "~/hooks/use-outside-pointerdown";
import {
  touchDeviceMiniDisplayedStartTimeVar,
  touchDeviceMiniDisplayedTime,
} from "~/util/mini-detail-displayed-time-for-datadog";

export const ItemPackage = (props: ItemPackageProps) => {
  const {
    src,
    alt,
    leftTag,
    rightTag,
    imgSupplementText,
    rankingOrder = 0,
    mainText,
    subText,
    overlayChildren,
    isOverlayTransparent = false,
    isOverlayClickable = false,
    width,
    height,
    borderRadius,
    bottomTag = null,
    aspectRatio,
    isFirstItem,
    isLastItem,
    previewModal,
    onItemHover,
    shouldShowPreviewModal = false,
    uniqueId,
    zIndex = UI_LIBRARY_Z_INDEX.PREVIEW_MODAL.toString(),
    onClick,
    mainTextLineClampNum = 1,
    subTextLineClampNum = 1,
    priceText,
    progressBarRate,
    progressBarColor = "YELLOW900",
    ratingStarScore,
    alternativeSrc,
    loading = "lazy",
    isShowRatingStarScoreNumber = false,
    ref,
    href,
    linkProps,
    ...args
  } = props;

  const { isSpSize, availableHover } = useMatchMedia();
  const [isShownByTouched, setIsShownByTouched] = useState(false);
  const [isMouseHover, setIsMouseHover] = useState(false);

  // MEMO: datadog タッチデバイス時のミニ末端の表示時間計測
  if (!isSpSize && !availableHover && isShownByTouched) touchDeviceMiniDisplayedStartTimeVar(new Date().getTime());

  const onOutsidePointerDown = useCallback(() => {
    setIsShownByTouched(false);
    // MEMO: datadog タッチデバイス時のミニ末端の表示時間計測
    if (!isSpSize && !availableHover && isShownByTouched) {
      touchDeviceMiniDisplayedTime({ startTime: touchDeviceMiniDisplayedStartTimeVar() });
    }
  }, [isShownByTouched]);

  const timer = useRef<number>();

  const { ref: wrapperRef } = useOutsidePointerdown<HTMLDivElement>({ onOutsidePointerDown });

  const onMouseOver = () => {
    window.clearTimeout(timer.current);
    if (!shouldShowPreviewModal) return;
    onItemHover && onItemHover(true);
    timer.current = window.setTimeout(() => {
      setIsMouseHover(true);
    }, MINI_DETAIL_STAND_TIME);
  };

  const onHideMiniDetail = useCallback(() => {
    if (!isSpSize && !availableHover && isShownByTouched) {
      setIsShownByTouched(false);
      onItemHover && onItemHover(false);
      // MEMO: datadog タッチデバイス時のミニ末端の表示時間計測
      touchDeviceMiniDisplayedTime({ startTime: touchDeviceMiniDisplayedStartTimeVar() });
    }
  }, [isShownByTouched, isSpSize, availableHover]);

  const onMouseOut = (e: MouseEvent) => {
    window.clearTimeout(timer.current);
    if (e.relatedTarget === null) return;
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (isInnerLazyImage(relatedTarget)) return;
    timer.current = window.setTimeout(() => {
      setIsMouseHover(false);
      // MEMO: カルーセル移動を繰り返しているとonItemHoverのfalseが先に動いてカルーセルのpadding要素が先になくなり見切れることがある。
      // false判定を少し遅らせてミニ末端のDOMが存在しているか判定してからfalse処理実行
      setTimeout(() => {
        if (document.getElementById(`mini-player-container`) === null) {
          onItemHover && onItemHover(false);
        }
      }, 100);
    }, MINI_DETAIL_STAND_TIME);
  };

  const isInnerLazyImage = (relatedTarget: HTMLElement) => {
    const elements = Array.from(document.getElementsByClassName(`lazy-image-wrapper-${uniqueId}`));
    return elements.some((item) => {
      return !("window" in relatedTarget) && item.contains(relatedTarget);
    });
  };

  const isRanking = rankingOrder > 0 && rankingOrder < 31;
  const isProgressBarRate = progressBarRate !== undefined;

  return (
    <Styled.DivWrapper ref={ref} width={width} onClick={onClick} onMouseOut={onMouseOut} {...args}>
      <div ref={wrapperRef}>
        <Styled.DivWrapperImg isRanking={rankingOrder > 0 && rankingOrder < 31}>
          <Styled.LazyImageWrapper
            canHover
            onMouseOver={onMouseOver}
            shouldShowPreviewModal={shouldShowPreviewModal}
            className={`lazy-image-wrapper-${uniqueId}`}
          >
            <div
              onClick={() => {
                if (shouldShowPreviewModal) {
                  setIsShownByTouched(true);
                  onItemHover && onItemHover(true);
                }
              }}
            >
              {href ? (
                <NextLink
                  href={href}
                  scroll={false}
                  onClick={(e) => {
                    if (shouldShowPreviewModal) {
                      e.preventDefault();
                    }
                  }}
                  {...linkProps}
                >
                  <Styled.LazyImage
                    src={src}
                    alternativeSrc={alternativeSrc}
                    alt={alt}
                    width={width}
                    height={height}
                    borderRadius={borderRadius}
                    aspectRatio={aspectRatio}
                    loading={loading}
                  />
                </NextLink>
              ) : (
                <Styled.LazyImage
                  src={src}
                  alternativeSrc={alternativeSrc}
                  alt={alt}
                  width={width}
                  height={height}
                  borderRadius={borderRadius}
                  aspectRatio={aspectRatio}
                  loading={loading}
                />
              )}
            </div>
            {/* スマホで縦横画面反転時にspサイズで消えるよう対応 */}
            {!isSpSize && (
              <PreviewModal
                isFirstItem={isFirstItem}
                isLastItem={isLastItem}
                zIndex={zIndex}
                availableHover={availableHover}
                isShownByTouched={isShownByTouched}
                onHideMiniDetail={onHideMiniDetail}
                previewModal={previewModal}
                isMouseHover={isMouseHover}
              />
            )}
          </Styled.LazyImageWrapper>
          {leftTag && <Styled.DivLeftTagWrapper>{leftTag}</Styled.DivLeftTagWrapper>}
          {rightTag && <Styled.DivRightTagWrapper>{rightTag}</Styled.DivRightTagWrapper>}
          {imgSupplementText && <Styled.SpanSupplement>{imgSupplementText}</Styled.SpanSupplement>}
          {isRanking && (
            <Styled.SpanRankingOrder aria-label={`${rankingOrder}位`} rankingOrder={rankingOrder}>
              {rankingOrder}
            </Styled.SpanRankingOrder>
          )}
          {isProgressBarRate && <Styled.ProgressBar progress={progressBarRate} bgColor={progressBarColor} />}
          {overlayChildren && (
            <Styled.DivOverlayWrapper
              isOverlayTransparent={isOverlayTransparent}
              isOverlayClickable={isOverlayClickable}
            >
              <Styled.DivOverlay>{overlayChildren}</Styled.DivOverlay>
            </Styled.DivOverlayWrapper>
          )}
        </Styled.DivWrapperImg>

        <Styled.DivDescription>
          {mainText && (
            <Styled.Heading size="S" lineClampNum={mainTextLineClampNum}>
              {mainText}
            </Styled.Heading>
          )}
          {subText && (
            <aside>
              <Styled.ParSubText lineClampNum={subTextLineClampNum}>{subText}</Styled.ParSubText>
            </aside>
          )}
          {ratingStarScore !== undefined && (
            <Styled.DivRatingStarWrapper>
              <RatingStar starScore={ratingStarScore} size={isSpSize ? "XS" : "M"} isReadOnly />
              {isShowRatingStarScoreNumber && ratingStarScore !== 0 && (
                <Styled.SpanRatingStarScore>{ratingStarScore.toFixed(1)}</Styled.SpanRatingStarScore>
              )}
            </Styled.DivRatingStarWrapper>
          )}
          {(bottomTag || priceText) && (
            <Styled.DivBottomLeftAdormentContainer hasBottomTag={!!bottomTag}>
              {bottomTag}
              {priceText && <Styled.DivPriceTextWrapper>{priceText}</Styled.DivPriceTextWrapper>}
            </Styled.DivBottomLeftAdormentContainer>
          )}
        </Styled.DivDescription>
      </div>
    </Styled.DivWrapper>
  );
};

ItemPackage.displayName = "ItemPackage";

const PreviewModal = (props: {
  availableHover: boolean;
  isFirstItem?: boolean;
  isLastItem?: boolean;
  zIndex: string;
  isShownByTouched: boolean;
  previewModal: ReactNode;
  isMouseHover?: boolean;
  onHideMiniDetail: () => void;
}) => {
  const {
    availableHover,
    isFirstItem,
    isLastItem,
    zIndex,
    previewModal,
    isShownByTouched,
    isMouseHover = true,
    onHideMiniDetail,
  } = props;
  return (
    <>
      {availableHover ? (
        <Styled.MouseDevicePreviewModalWrapper
          data-type="previewModal"
          isFirstItem={isFirstItem}
          isLastItem={isLastItem}
          isMouseHover={isMouseHover}
          zIndex={zIndex}
        >
          {previewModal}
        </Styled.MouseDevicePreviewModalWrapper>
      ) : (
        <Styled.TouchDevicePreviewModalWrapper
          data-type="previewModal"
          isFirstItem={isFirstItem}
          isLastItem={isLastItem}
          zIndex={zIndex}
          isShownByTouched={isShownByTouched}
          isMouseHover={isMouseHover}
          onClick={onHideMiniDetail}
        >
          <Styled.TouchDevicePreviewModalBody>{isShownByTouched && previewModal}</Styled.TouchDevicePreviewModalBody>
        </Styled.TouchDevicePreviewModalWrapper>
      )}
    </>
  );
};
